import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useLoginPage } from './useLoginPage';
import { fadeInUp } from './loginConstants';
import LegalAgreementLine from '../../components/LegalAgreementLine';
import { APP_NAME } from '../../config/region';

const LoginPage: React.FC = () => {
  const {
    email,
    handleEmailChange,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    error,
    loading,
    emailStatus,
    isTouched,
    setIsTouched,
    handleSubmit,
  } = useLoginPage();

  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [controls, isInView]);

  return (
    <div className="min-h-screen bg-slate-950">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white min-h-screen flex items-center"
      >
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
          <motion.div
            className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/5 backdrop-blur-sm border border-white/10 mb-6"
              >
                <span className="h-2 w-2 rounded-full bg-cyan-400 mr-2"></span>
                Welcome back to {APP_NAME}
              </motion.div>
              <motion.h1
                className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <span className="block">Login to Your</span>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Account</span>
              </motion.h1>
              <motion.p
                className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Sign in to access your dashboard and continue growing your business with {APP_NAME}.
              </motion.p>
              <motion.div
                className="flex items-center justify-center lg:justify-start space-x-4 mt-8 lg:mt-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link to="/" className="inline-flex items-center text-sm text-slate-300 hover:text-white">
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back to Home
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              ref={ref}
              initial="hidden"
              animate={controls}
              variants={fadeInUp}
              className="bg-slate-900/75 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Sign In</h2>
              <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
                {error && (
                  <div className="bg-red-500/20 border border-red-400 text-red-100 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-slate-200 mb-1">
                    Email address
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={() => setIsTouched((prev) => ({ ...prev, email: true }))}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-950/70 border border-white/10 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base pr-10 sm:pr-12"
                      placeholder="you@example.com"
                    />
                    {isTouched.email && (
                      <div className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center pointer-events-none">
                        {emailStatus.checking ? (
                          <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : emailStatus.available === false ? (
                          <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                        ) : emailStatus.available === true ? (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        ) : null}
                      </div>
                    )}
                  </div>
                  {isTouched.email && emailStatus.available === true && (
                    <p className="mt-1 text-xs sm:text-sm text-red-300">No account found with this email. Please sign up.</p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-xs font-medium text-slate-200 mb-1">
                      Password
                    </label>
                    <Link to="/reset-password" className="text-xs sm:text-sm font-medium text-cyan-400 hover:text-cyan-300">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-950/70 border border-white/10 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 pr-10 sm:pr-12 text-sm sm:text-base"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 hover:text-white" />
                      ) : (
                        <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 hover:text-white" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center items-center py-2.5 sm:py-3 px-4 sm:px-6 border border-transparent rounded-lg shadow-sm text-sm sm:text-base font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading ? (
                      'Signing in...'
                    ) : (
                      <>
                        Sign in
                        <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
              <div className="mt-4 sm:mt-6 text-center">
                <p className="text-xs sm:text-sm text-slate-300">
                  Don't have an account?{' '}
                  <Link to="/signup" className="font-medium text-cyan-400 hover:text-cyan-300">
                    Sign up for free
                  </Link>
                </p>
              </div>
              <div className="mt-6 sm:mt-8 border-t border-white/10 pt-4 sm:pt-6">
                <LegalAgreementLine prefix="By signing in, you agree to our" />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default LoginPage;
